import { createFileRoute, redirect } from "@tanstack/react-router";
import { formBuilderQueryOptions, useFormBuilder } from "@/features/forms/api/formBuilderApi";
import FormBuilder, { BUILDER_TABS, type BuilderTab } from "@/features/forms/components/form-builder/FormBuilder";
import { formBuilderSearchSchema } from "@/features/forms/components/form-builder/formBuilderSearch";
import { formBreadcrumbLabel } from "@/features/navigation/services/entityDisplayLabel";
import { useNav } from "@/utils/nav";

/** "womac-osteoarthritis-index" -> "Womac Osteoarthritis Index". */
const capitalCaseSlug = (slug: string | null | undefined) =>
    (slug ?? "")
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

const isBuilderTab = (value: string): value is BuilderTab => (BUILDER_TABS as readonly string[]).includes(value);

function FormBuilderTabPage() {
    const { formId, tab } = Route.useParams();
    const { data: form } = useFormBuilder(formId);
    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    useNav([
        {
            label: "Builder",
            route: { to: "/builder" },
        },
        {
            label: "Forms",
            route: { to: "/builder/forms" },
        },
        {
            // The slug is short and unique; the publication badge sits in the builder's top bar.
            label: capitalCaseSlug(form.slug) || formBreadcrumbLabel(form),
            route: { to: "/builder/forms/$formId/$tab", params: { formId, tab: "questions" } },
        },
    ]);

    return (
        <FormBuilder
            formId={formId}
            search={search}
            onSelectionChange={(next, options) => void navigate({ search: next, replace: options?.replace })}
            tab={tab as BuilderTab}
            onTabChange={(next) => void navigate({ params: { formId, tab: next }, search: (prev) => prev })}
        />
    );
}

export const Route = createFileRoute("/_app/builder/forms/$formId/$tab")({
    component: FormBuilderTabPage,
    validateSearch: (search) => formBuilderSearchSchema.parse(search),
    beforeLoad: ({ params }) => {
        if (!isBuilderTab(params.tab)) {
            throw redirect({ to: "/builder/forms/$formId/$tab", params: { formId: params.formId, tab: "questions" } });
        }
    },
    loader: async ({ context, params }) => {
        await context?.queryClient.ensureQueryData(formBuilderQueryOptions(params.formId));
    },
});
